import axiosInstance from './axiosInstance';
import authTokenHandler from '@/utils/authTokenHandler';
import tokenService from './tokenService';
import authSessionIdentity from '@/data/auth/authSessionIdentity';
import usersApi from './users';

const authService = new EventTarget();

Object.assign(authService, {
  signup(email, password) {
    return axiosInstance.post('/register', {
      email,
      password
    });
  },

  /**
   * Attempts to log in with the provided email and password, securely
   * stores the token.
   * @param {string} email - The user's email.
   * @param {string} password - The user's password.
   * @returns {Promise<void>} A promise that resolves if login and token
   * storage succeed.
   */
  async login(email, password) {
    // Explicit credentials may switch users without an intervening logout,
    // so remove the previous namespace before any new local read can start.
    authSessionIdentity.clear();

    return axiosInstance
      .post('/oauth/token', {
        grant_type: 'password',
        email,
        password
      })
      .then(tokenService.handleTokenResponse)
      .then(usersApi.getCurrentUserId)
      .then((userId) => {
        authService.dispatchEvent(
          new CustomEvent('auth:session-changed', {
            detail: { userId }
          })
        );
        return undefined;
      });
  },

  validateAndRefreshToken() {
    return authTokenHandler.retrieveToken().then((token_data) => {
      const isValid = authTokenHandler.isTokenValid(token_data);
      if (isValid) {
        return usersApi.ensureStoredUserId();
      }
      return tokenService
        .refreshToken(axiosInstance)
        .then(usersApi.ensureStoredUserId);
    });
  },

  /**
   * Restores usable authentication state for navigation flows when possible.
   *
   * Route guards cannot rely on the access token alone, because a fresh page
   * load may arrive after that token expires while the refresh token can still
   * recover the user's authenticated state. This helper keeps that policy in
   * one place and resolves to a boolean so guards can branch cleanly.
   *
   * @returns {Promise<boolean>}
   *   Resolves to `true` when authentication is already usable or can be
   *   restored through the refresh flow, otherwise resolves to `false`.
   */
  restoreAuth() {
    return authTokenHandler
      .retrieveToken()
      .then((token_data) => {
        if (authTokenHandler.isTokenValid(token_data)) {
          return usersApi.ensureStoredUserId().then(() => true);
        }

        return tokenService
          .refreshToken(axiosInstance)
          .then(usersApi.ensureStoredUserId)
          .then(() => true);
      })
      .catch(() => false);
  },

  /**
   * Determines whether a stored authentication token exists and is still valid.
   *
   * This method attempts to retrieve the token asynchronously, then checks its
   * expiration status. It will resolve to `true` if a non-expired token is found,
   * or `false` if no token is present, the token is expired, or any error occurs
   * during retrieval/validation.
   *
   * @returns {Promise<boolean>}
   *   A promise that resolves to:
   *   - `true` if a valid (non-expired) token exists
   *   - `false` otherwise
   */
  async hasValidToken() {
    return authTokenHandler
      .retrieveToken()
      .then((token_data) => authTokenHandler.isTokenValid(token_data))
      .catch(() => false);
  },

  /**
   * Clears stored tokens from local storage, effectively logging the user out.
   * @returns {void}
   */
  logout: function () {
    authTokenHandler.clearStoredToken();
    authSessionIdentity.clear();
    authService.dispatchEvent(new Event('auth:logout'));
  }
});

export default authService;
